import { CompoundFeedNameDatabase } from "../../components/FarmFlow/Baseline/types";
import { AnimalSourceOrigin } from "../Baseline";

import {
    CattleManureManagementSystemType,
    InternalSource as BaselineInternalSource,
    ExternalSource as BaselineExternalSource,
} from "../Baseline/CattleBaseline";

import {
    InterventionPropertyChangeMetric,
    InterventionPropertyValue,
    InterventionV2,
    Stage,
} from "./Intervention";

import {
    ProcessingStage,
    ProcessingStageData
} from "./Processing";

import {
    CattleFeed as InterventionCattleFeed,
    CattleHousingAndManure as InterventionCattleHousingAndManure,
    CattleEmission as InterventionCattleEmission,
    InternalSource as InterventionInternalSource,
    ExternalSource as InterventionExternalSource,
} from "./CattleIntervention";

export const availableMmsTypeForMonthlyStoragePeriods = [
    CattleManureManagementSystemType.LiquidCover,
    CattleManureManagementSystemType.LiquidCrust,
    CattleManureManagementSystemType.LiquidNoCrust,
    CattleManureManagementSystemType.Pit,
];

export const availableMmsTypeForOverOrUnderStoragePeriod = [
    CattleManureManagementSystemType.DeepBeddingActiveMixing,
    CattleManureManagementSystemType.DeepBeddingNoMixing,
];

export interface DairyV2Input {
    cattleProductivity: "HIGH" | "LOW";
    startDate?: Date | string;

    animalsPresentAtStart: InterventionPropertyValue;
    animalsPresentAtStart_newValue: InterventionPropertyValue;
    animalsPresentAtStart_changeMetric: InterventionPropertyChangeMetric;

    animalsPresentAtEnd: InterventionPropertyValue;
    animalsPresentAtEnd_newValue: InterventionPropertyValue;
    animalsPresentAtEnd_changeMetric: InterventionPropertyChangeMetric;

    averageWeightAtStart: InterventionPropertyValue;
    averageWeightAtStart_newValue: InterventionPropertyValue;
    averageWeightAtStart_changeMetric: InterventionPropertyChangeMetric;

    averageWeightNewAnimals: InterventionPropertyValue;
    averageWeightNewAnimals_newValue: InterventionPropertyValue;
    averageWeightNewAnimals_changeMetric: InterventionPropertyChangeMetric;

    internalSources?: InterventionInternalSource[] | null;
    externalSources?: InterventionExternalSource[] | null;
    internalSourcesAdditions?: BaselineInternalSource[] | null;
    externalSourcesAdditions?: BaselineExternalSource[] | null;
}

export interface DairyV2InputContinuousGrowing extends DairyV2Input {
    averageWeightAtEnd?: InterventionPropertyValue;
    averageWeightAtEnd_newValue?: InterventionPropertyValue;
    averageWeightAtEnd_changeMetric?: InterventionPropertyChangeMetric;

    averageAge?: InterventionPropertyValue;
    averageAge_newValue?: InterventionPropertyValue;
    averageAge_changeMetric?: InterventionPropertyChangeMetric;
}

export interface DairyV2Output {
    cowsToAnotherStage: InterventionPropertyValue;
    cowsToAnotherStage_newValue: InterventionPropertyValue;
    cowsToAnotherStage_changeMetric: InterventionPropertyChangeMetric;

    averageWeightOfCowsLeavingTheStage: InterventionPropertyValue;
    averageWeightOfCowsLeavingTheStage_newValue: InterventionPropertyValue;
    averageWeightOfCowsLeavingTheStage_changeMetric: InterventionPropertyChangeMetric;

    cowsSold: InterventionPropertyValue;
    cowsSold_newValue: InterventionPropertyValue;
    cowsSold_changeMetric: InterventionPropertyChangeMetric;

    pricePerCowSold?: InterventionPropertyValue;
    pricePerCowSold_newValue?: InterventionPropertyValue;
    pricePerCowSold_changeMetric?: InterventionPropertyChangeMetric;

    numberOfMortalitiesCows: InterventionPropertyValue;
    numberOfMortalitiesCows_newValue: InterventionPropertyValue;
    numberOfMortalitiesCows_changeMetric: InterventionPropertyChangeMetric;

    averageWeightAtMortalityCows: InterventionPropertyValue;
    averageWeightAtMortalityCows_newValue: InterventionPropertyValue;
    averageWeightAtMortalityCows_changeMetric: InterventionPropertyChangeMetric;
}

export interface DairyV2OutputCalves {
    weanedCalvesToAnotherStage: InterventionPropertyValue;
    weanedCalvesToAnotherStage_newValue: InterventionPropertyValue;
    weanedCalvesToAnotherStage_changeMetric: InterventionPropertyChangeMetric;

    weanedCalvesSold: InterventionPropertyValue;
    weanedCalvesSold_newValue: InterventionPropertyValue;
    weanedCalvesSold_changeMetric: InterventionPropertyChangeMetric;

    averageWeightAtBirth?: InterventionPropertyValue;
    averageWeightAtBirth_newValue?: InterventionPropertyValue;
    averageWeightAtBirth_changeMetric?: InterventionPropertyChangeMetric;
}

export interface DairyV2MilkProduction {
    averageMilkFat: InterventionPropertyValue;
    averageMilkFat_newValue: InterventionPropertyValue;
    averageMilkFat_changeMetric: InterventionPropertyChangeMetric;

    averageMilkProtein: InterventionPropertyValue;
    averageMilkProtein_newValue: InterventionPropertyValue;
    averageMilkProtein_changeMetric: InterventionPropertyChangeMetric;

    yearlyMilkProduction: InterventionPropertyValue;
    yearlyMilkProduction_newValue: InterventionPropertyValue;
    yearlyMilkProduction_changeMetric: InterventionPropertyChangeMetric;
}

export type DairyV2OutputDairyCow = DairyV2Output & DairyV2OutputCalves & DairyV2MilkProduction;

export interface DairyV2OutputContinuousGrowing extends DairyV2Output {
    averageAge: InterventionPropertyValue;
    averageAge_newValue: InterventionPropertyValue;
    averageAge_changeMetric: InterventionPropertyChangeMetric;
}

export interface DairyV2Emissions {
    emissionMitigations?: InterventionCattleEmission;
    NOP?: boolean;

    NOPdose?: InterventionPropertyValue;
    NOPdose_newValue?: InterventionPropertyValue;
    NOPdose_changeMetric?: InterventionPropertyChangeMetric;

    averageFatDMI?: InterventionPropertyValue;
    averageFatDMI_newValue?: InterventionPropertyValue;
    averageFatDMI_changeMetric?: InterventionPropertyChangeMetric;

    cowsFedSupplement?: InterventionPropertyValue;
    cowsFedSupplement_newValue?: InterventionPropertyValue;
    cowsFedSupplement_changeMetric?: InterventionPropertyChangeMetric;

    calculatedMethaneFermentation?: InterventionPropertyValue;
    calculatedMethaneFermentation_newValue?: InterventionPropertyValue;
    calculatedMethaneFermentation_changeMetric?: InterventionPropertyChangeMetric;

    totalMethaneFermentation?: InterventionPropertyValue;
    totalMethaneFermentation_newValue?: InterventionPropertyValue;
    totalMethaneFermentation_changeMetric?: InterventionPropertyChangeMetric;
}

export interface DairyV2CommonStageData {
    feed: InterventionCattleFeed;
    housing: InterventionCattleHousingAndManure;
    emissions?: DairyV2Emissions;
}
export interface DairyV2DairyCowStageData extends DairyV2CommonStageData {
    input: DairyV2Input;
    output: DairyV2OutputDairyCow;
}

export interface DairyV2ContinuousGrowingStageData extends DairyV2CommonStageData {
    input: DairyV2InputContinuousGrowing;
    output: DairyV2OutputContinuousGrowing;
}
export interface DairyV2Stage extends Stage {
    startDate?: string;
    stageData:
    | DairyV2ContinuousGrowingStageData
    | DairyV2DairyCowStageData
    | ProcessingStageData;
}

export interface DairyV2Intervention extends InterventionV2 {
    stages: Array<DairyV2Stage | ProcessingStage>;
}

export interface DairyV2NutritionalData {
    type: string;
    value: InterventionPropertyValue;
}

export interface DairyV2CompoundFeedNameDatabase extends CompoundFeedNameDatabase {
    nutritionalAnalysisData: DairyV2NutritionalData[];
}
